import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

function formatDate(d) {
  if (!d) return '–';
  const date = new Date(d);
  return isNaN(date.getTime()) ? '–' : date.toLocaleDateString('nb-NO', { dateStyle: 'short' });
}

function formatAmount(n) {
  if (n == null || Number.isNaN(Number(n))) return '–';
  return Number(n).toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function getStatus(upload) {
  if (upload.voucher_number || upload.voucher_id) {
    return { label: 'Posted', detail: upload.voucher_number ? `#${upload.voucher_number}` : `ID ${upload.voucher_id}`, status: 'posted' };
  }
  if (upload.upload_response?.status === 'error') {
    return { label: 'Failed', detail: upload.upload_response?.message || 'Error', status: 'failed' };
  }
  return { label: 'Pending', detail: null, status: 'pending' };
}

const VouchersUploadedPage = () => {
  const [uploads, setUploads] = useState([]);
  const [linesByUploadId, setLinesByUploadId] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      const { data: uploadsData, error: uploadsError } = await supabase
        .from('voucher_uploads_to_tripletex')
        .select('id, created_at, description, voucher_date, attachment, voucher_id, voucher_number, upload_response')
        .order('created_at', { ascending: false });

      if (uploadsError) {
        if (!cancelled) setError(uploadsError.message);
        setLoading(false);
        return;
      }

      if (cancelled) return;
      setUploads(uploadsData || []);

      if (!uploadsData?.length) {
        setLinesByUploadId({});
        setLoading(false);
        return;
      }

      const ids = uploadsData.map((u) => u.id);
      const { data: linesData, error: linesError } = await supabase
        .from('voucher_lines_uploads_to_tripletex')
        .select('id, voucher_uploads_to_tripletex, line_description, amount, account, department')
        .in('voucher_uploads_to_tripletex', ids)
        .order('id', { ascending: true });

      if (linesError) {
        if (!cancelled) setError(linesError.message);
      } else if (!cancelled && linesData) {
        const byUpload = {};
        linesData.forEach((line) => {
          const uid = line.voucher_uploads_to_tripletex;
          if (!byUpload[uid]) byUpload[uid] = [];
          byUpload[uid].push(line);
        });
        setLinesByUploadId(byUpload);
      }
      setLoading(false);
    }

    load();
    return () => { cancelled = true; };
  }, []);

  const statusBadge = (status) => {
    const styles = {
      posted: 'bg-gray-100 text-black',
      pending: 'bg-gray-100 text-gray-500',
      failed: 'bg-red-50 text-red-700',
    };
    return styles[status] || 'bg-gray-100 text-gray-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-4xl mx-auto px-6 py-10">
          <p className="text-sm text-gray-400">Loading voucher uploads...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <header className="mb-8">
          <h1 className="text-xl font-semibold text-black">Vouchers uploaded</h1>
          <p className="mt-1 text-sm text-gray-400">
            Status of voucher uploads to Tripletex.
          </p>
        </header>

        {error && (
          <div className="border border-red-200 rounded-md p-4 mb-6 text-sm text-red-600">
            {error}
          </div>
        )}

        {uploads.length === 0 && !error && (
          <div className="border border-gray-200 rounded-md p-8 text-center text-sm text-gray-400">
            No voucher uploads yet.
          </div>
        )}

        {uploads.length > 0 && (
          <div className="space-y-2">
            {uploads.map((upload) => {
              const status = getStatus(upload);
              const lines = linesByUploadId[upload.id] || [];
              const isExpanded = expandedId === upload.id;

              return (
                <div
                  key={upload.id}
                  className="border border-gray-200 rounded-md overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => setExpandedId(isExpanded ? null : upload.id)}
                    className="w-full px-5 py-3 flex items-center justify-between gap-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-black truncate">
                        {upload.description || `Upload #${upload.id}`}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Voucher date: {formatDate(upload.voucher_date)}
                        {upload.attachment && (
                          <span className="ml-3">Attachment: {upload.attachment}</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${statusBadge(status.status)}`}>
                        {status.label}
                        {status.detail && ` ${status.detail}`}
                      </span>
                      <span className="text-gray-300 text-xs">
                        {isExpanded ? '▼' : '▶'}
                      </span>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="border-t border-gray-200 bg-gray-50/50">
                      <div className="px-5 py-3">
                        <h3 className="text-xs font-medium text-gray-500 mb-2">
                          Lines ({lines.length})
                        </h3>
                        {lines.length === 0 ? (
                          <p className="text-xs text-gray-400">No lines.</p>
                        ) : (
                          <div className="overflow-x-auto border border-gray-200 rounded-md bg-white">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">Description</th>
                                  <th className="text-right py-2 px-3 text-xs font-medium text-gray-500">Amount</th>
                                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">Account</th>
                                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">Department</th>
                                </tr>
                              </thead>
                              <tbody>
                                {lines.map((line) => (
                                  <tr key={line.id} className="border-t border-gray-100">
                                    <td className="py-2 px-3 text-gray-700">{line.line_description || '–'}</td>
                                    <td className="py-2 px-3 text-right tabular-nums text-gray-700">{formatAmount(line.amount)}</td>
                                    <td className="py-2 px-3 text-gray-500">{line.account ?? '–'}</td>
                                    <td className="py-2 px-3 text-gray-500">{line.department ?? '–'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default VouchersUploadedPage;
